import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  getDoc,
  collectionGroup,
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../../../firebase";
import styles from "../../../styles/AdminConsole.module.css";
import { useToast } from "../shared/hooks/useToast";
import { useDragScroll } from "../shared/hooks/useDragScroll";
import { useUploadToR2 } from "../shared/hooks/useUploadToR2";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import Pagination from "../shared/components/Pagination";
import QualityTable from "./QualityTable";
import AddOrderModal from "./modals/AddOrderModal";
import EditOrderModal from "./modals/EditOrderModal";
import AddVisitModal from "./modals/AddVisitModal";
import EditVisitModal from "./modals/EditVisitModal";

// ── Constants ────────────────────────────────────────────────────────────────
const LOGO_URL =
  "https://cdn.synaptexglobal.com/Home%20(3240%20x%201080%20px).png";
const ITEMS_PER_PAGE = 15;

const EMPTY_ORDER_FORM = { orderId: "", supplier: "", country: "", city: "" };
const EMPTY_VISIT_FORM = {
  milestone: "Fabric Inspection",
  date: "",
  description: "",
  concern: "",
  cleared: false,
  paidINR: "",
  documents: [],
};

// ── Component ────────────────────────────────────────────────────────────────
export default function Quality() {
  const { uid: routeUid } = useParams();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();
  const { scrollRef, onMouseDown, onMouseMove, onMouseUp } = useDragScroll();
  const { uploadToR2 } = useUploadToR2();

  // ── Auth ──────────────────────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [visitsByOrder, setVisitsByOrder] = useState({});
  const visitUnsubsRef = useRef({});
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [allOrders, setAllOrders] = useState([]); // for order-ID dropdown

  // ── Filters / pagination ──────────────────────────────────────────────────
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // ── Add Order modal ───────────────────────────────────────────────────────
  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [orderForm, setOrderForm] = useState(EMPTY_ORDER_FORM);
  const [savingOrder, setSavingOrder] = useState(false);

  // ── Edit Order modal ──────────────────────────────────────────────────────
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderForm, setEditOrderForm] = useState(EMPTY_ORDER_FORM);
  const [editOrderSaving, setEditOrderSaving] = useState(false);

  // ── Add Visit modal ───────────────────────────────────────────────────────
  const [addVisitOrder, setAddVisitOrder] = useState(null);
  const [visitForm, setVisitForm] = useState(EMPTY_VISIT_FORM);
  const [visitDocInput, setVisitDocInput] = useState("");
  const [savingVisit, setSavingVisit] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // ── Edit Visit modal ──────────────────────────────────────────────────────
  const [editingVisit, setEditingVisit] = useState(null);
  const [editVisitForm, setEditVisitForm] = useState(EMPTY_VISIT_FORM);
  const [editVisitDocInput, setEditVisitDocInput] = useState("");
  const [editVisitSaving, setEditVisitSaving] = useState(false);
  const [editUploadingFiles, setEditUploadingFiles] = useState(false);

  // ── Delete confirm ────────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isAdmin = userProfile?.isAdmin === true;
  const isQC = userProfile?.category === "quality-control";
  const canWrite = isAdmin || isQC;

  // ── Auth listener ─────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      const profile = { uid: user.uid, ...(snap.data() || {}) };
      if (!profile.isAdmin && profile.category !== "quality-control") {
        navigate("/");
        return;
      }
      if (!profile.isAdmin && user.uid !== routeUid) {
        navigate(`/quality/${user.uid}`);
        return;
      }
      setCurrentUser(user);
      setUserProfile(profile);
      setAuthReady(true);
    });
    return () => unsub();
  }, [routeUid, navigate]);

  // ── Live master orders ────────────────────────────────────────────────────
  useEffect(() => {
    if (!authReady) return;
    const q = query(
      collection(db, "users", routeUid, "qualityReports"),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q, (snap) =>
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, [authReady, routeUid]);

  // ── Live visits — one nested listener per order ───────────────────────────
  useEffect(() => {
    if (!authReady) return;
    const currentIds = new Set(orders.map((o) => o.id));
    // Tear down stale listeners
    Object.keys(visitUnsubsRef.current).forEach((orderId) => {
      if (!currentIds.has(orderId)) {
        visitUnsubsRef.current[orderId]();
        delete visitUnsubsRef.current[orderId];
        setVisitsByOrder((prev) => {
          const next = { ...prev };
          delete next[orderId];
          return next;
        });
      }
    });
    // Start new listeners
    orders.forEach((order) => {
      if (visitUnsubsRef.current[order.id]) return;
      const q = query(
        collection(db, "users", routeUid, "qualityReports", order.id, "visits"),
        orderBy("date", "desc"),
      );
      visitUnsubsRef.current[order.id] = onSnapshot(q, (snap) => {
        setVisitsByOrder((prev) => ({
          ...prev,
          [order.id]: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
        }));
      });
    });
  }, [orders, authReady, routeUid]);

  // Tear down all visit listeners on unmount
  useEffect(
    () => () => {
      Object.values(visitUnsubsRef.current).forEach((fn) => fn());
      visitUnsubsRef.current = {};
    },
    [],
  );

  // ── All orders (for Order ID dropdown) ───────────────────────────────────
  useEffect(() => {
    if (!authReady) return;
    const q = query(
      collectionGroup(db, "orders"),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q, (snap) => {
      setAllOrders(
        snap.docs
          .map((d) => ({
            id: d.id,
            _ownerUid: d.ref.parent.parent.id,
            _ownerName: d.data().Buyer?.name || "",
            ...d.data(),
          }))
          .filter((o) => o.orderNumber?.trim()),
      );
    });
  }, [authReady]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const getOrderStats = useCallback(
    (orderId) => {
      const visits = visitsByOrder[orderId] || [];
      const totalPaid = visits.reduce(
        (sum, v) => sum + (Number(v.paidINR) || 0),
        0,
      );
      const open = visits.filter((v) => !v.cleared).length;
      return {
        visitCount: visits.length,
        open,
        totalPaid,
        isFullyCleared: visits.length > 0 && open === 0,
      };
    },
    [visitsByOrder],
  );

  const filteredOrders = useMemo(
    () =>
      orders.filter((o) => {
        if (filterCountry !== "all" && o.country !== filterCountry)
          return false;
        if (filterStatus !== "all") {
          const { visitCount, isFullyCleared } = getOrderStats(o.id);
          if (filterStatus === "open" && (visitCount === 0 || isFullyCleared))
            return false;
          if (filterStatus === "cleared" && !isFullyCleared) return false;
        }
        return true;
      }),
    [orders, filterCountry, filterStatus, getOrderStats],
  );

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = useMemo(
    () =>
      filteredOrders.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      ),
    [filteredOrders, currentPage],
  );

  const stats = useMemo(() => {
    const allVisits = Object.values(visitsByOrder).flat();
    return {
      totalOrders: orders.length,
      totalVisits: allVisits.length,
      open: allVisits.filter((v) => !v.cleared).length,
      cleared: allVisits.filter((v) => v.cleared).length,
      totalPaid: allVisits.reduce(
        (sum, v) => sum + (Number(v.paidINR) || 0),
        0,
      ),
      recent: allVisits.filter((v) => {
        if (!v.date) return false;
        const d = v.date.toDate ? v.date.toDate() : new Date(v.date);
        return Date.now() - d.getTime() < 30 * 24 * 60 * 60 * 1000;
      }).length,
    };
  }, [orders, visitsByOrder]);

  const presentCountries = useMemo(
    () => [...new Set(orders.map((o) => o.country).filter(Boolean))].sort(),
    [orders],
  );
  const availableOrderOptions = useMemo(
    () =>
      allOrders.filter(
        (o) => !orders.some((existing) => existing.id === o.orderNumber),
      ),
    [allOrders, orders],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleOrderSelect = useCallback(
    (orderNumber, setForm) => {
      const order = allOrders.find((o) => o.orderNumber === orderNumber);
      const supplier = order?.Suppliers?.[0]?.name || "";
      const rawLoc = order?.Suppliers?.[0]?.location || "";
      const parts = rawLoc.split(",").map((s) => s.trim());
      const country = parts[parts.length - 1] || "";
      setForm((f) => ({ ...f, orderId: orderNumber, supplier, country }));
    },
    [allOrders],
  );

  const toggleExpand = useCallback(
    (orderId) =>
      setExpandedOrderId((cur) => (cur === orderId ? null : orderId)),
    [],
  );

  const handleAddOrder = useCallback(async () => {
    const orderId = orderForm.orderId.trim();
    if (!orderId || !orderForm.supplier.trim() || !orderForm.country) {
      showToast("Order ID, Supplier and Country are required.", "error");
      return;
    }
    if (orderId.includes("/")) {
      showToast("Order ID can't contain a '/' character.", "error");
      return;
    }
    if (orders.some((o) => o.id === orderId)) {
      showToast(
        "This order already has a QC record — use its + button to log a visit.",
        "error",
      );
      return;
    }
    setSavingOrder(true);
    try {
      await setDoc(doc(db, "users", routeUid, "qualityReports", orderId), {
        orderId,
        supplier: orderForm.supplier.trim(),
        country: orderForm.country,
        city: orderForm.city.trim(),
        createdAt: serverTimestamp(),
      });
      showToast("Order added — use + to log its first visit.");
      setOrderForm(EMPTY_ORDER_FORM);
      setShowAddOrderModal(false);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSavingOrder(false);
    }
  }, [orderForm, orders, routeUid, showToast]);

  const handleOpenEditOrder = useCallback((order) => {
    setEditingOrder(order);
    setEditOrderForm({
      orderId: order.id,
      supplier: order.supplier || "",
      country: order.country || "",
      city: order.city || "",
    });
  }, []);

  const handleSaveEditOrder = useCallback(async () => {
    if (!editingOrder) return;
    if (!editOrderForm.supplier.trim() || !editOrderForm.country) {
      showToast("Supplier and Country are required.", "error");
      return;
    }
    setEditOrderSaving(true);
    try {
      await updateDoc(
        doc(db, "users", routeUid, "qualityReports", editingOrder.id),
        {
          supplier: editOrderForm.supplier.trim(),
          country: editOrderForm.country,
          city: editOrderForm.city.trim(),
        },
      );
      showToast("Order updated.");
      setEditingOrder(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setEditOrderSaving(false);
    }
  }, [editingOrder, editOrderForm, routeUid, showToast]);

  const handleDeleteOrder = useCallback(
    async (order) => {
      const key = `order:${order.id}`;
      if (deleteConfirm !== key) {
        setDeleteConfirm(key);
        return;
      }
      try {
        if (isAdmin) {
          // Admin deletes are immediate — order + all its visits, cascade.
          const visits = visitsByOrder[order.id] || [];
          await Promise.all(
            visits.map((v) =>
              deleteDoc(
                doc(
                  db,
                  "users",
                  routeUid,
                  "qualityReports",
                  order.id,
                  "visits",
                  v.id,
                ),
              ),
            ),
          );
          await deleteDoc(
            doc(db, "users", routeUid, "qualityReports", order.id),
          );
          setDeleteConfirm(null);
          if (expandedOrderId === order.id) setExpandedOrderId(null);
          showToast(`Order "${order.id}" and its visits deleted.`, "error");
        } else {
          // QC member: raise a delete request for admin review instead of
          // deleting directly. The order (and its visits) stay fully active
          // until an admin approves the request from the QC Flags tab.
          const ownerSnap = await getDoc(doc(db, "users", routeUid));
          const ownerName = ownerSnap.data()?.name || routeUid;
          await Promise.all([
            updateDoc(doc(db, "users", routeUid, "qualityReports", order.id), {
              deleteRequested: true,
            }),
            setDoc(doc(db, "qcFlagIndex", `${order.id}_delete`), {
              type: "delete_request",
              scope: "order",
              ownerUid: routeUid,
              ownerName,
              orderId: order.id,
              supplier: order.supplier || "",
              country: order.country || "",
              status: "pending",
              requestedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
            }),
          ]);
          setDeleteConfirm(null);
          showToast("Delete request sent to admin.", "error");
        }
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [
      deleteConfirm,
      visitsByOrder,
      routeUid,
      expandedOrderId,
      showToast,
      isAdmin,
    ],
  );

  const handleCancelOrderDeleteRequest = useCallback(
    async (order) => {
      try {
        await Promise.all([
          deleteDoc(doc(db, "qcFlagIndex", `${order.id}_delete`)),
          updateDoc(doc(db, "users", routeUid, "qualityReports", order.id), {
            deleteRequested: false,
          }),
        ]);
        showToast("Delete request cancelled.");
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [routeUid, showToast],
  );

  const handleOpenAddVisit = useCallback((order) => {
    setAddVisitOrder(order);
    setVisitForm(EMPTY_VISIT_FORM);
    setVisitDocInput("");
  }, []);

  const handleAddVisit = useCallback(async () => {
    if (!addVisitOrder) return;
    if (!visitForm.milestone || !visitForm.date) {
      showToast("Milestone and Visit Date are required.", "error");
      return;
    }
    setSavingVisit(true);
    try {
      await addDoc(
        collection(
          db,
          "users",
          routeUid,
          "qualityReports",
          addVisitOrder.id,
          "visits",
        ),
        {
          milestone: visitForm.milestone,
          date: Timestamp.fromDate(new Date(visitForm.date)),
          description: visitForm.description.trim(),
          concern: visitForm.concern.trim(),
          cleared: visitForm.cleared,
          paidINR: visitForm.paidINR === "" ? 0 : Number(visitForm.paidINR),
          documents: visitForm.documents,
          createdAt: serverTimestamp(),
        },
      );
      showToast("Visit logged.");
      setExpandedOrderId(addVisitOrder.id);
      setAddVisitOrder(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSavingVisit(false);
    }
  }, [addVisitOrder, visitForm, routeUid, showToast]);

  const handleOpenEditVisit = useCallback((orderId, visit) => {
    setEditingVisit({ orderId, ...visit });
    setEditVisitForm({
      milestone: visit.milestone || "Fabric Inspection",
      date: visit.date ? visit.date.toDate().toISOString().split("T")[0] : "",
      description: visit.description || "",
      concern: visit.concern || "",
      cleared: visit.cleared ?? false,
      paidINR: visit.paidINR ?? "",
      documents: visit.documents || [],
    });
    setEditVisitDocInput("");
  }, []);

  const handleSaveEditVisit = useCallback(async () => {
    if (!editingVisit) return;
    if (!editVisitForm.milestone || !editVisitForm.date) {
      showToast("Milestone and Visit Date are required.", "error");
      return;
    }
    setEditVisitSaving(true);
    try {
      await updateDoc(
        doc(
          db,
          "users",
          routeUid,
          "qualityReports",
          editingVisit.orderId,
          "visits",
          editingVisit.id,
        ),
        {
          milestone: editVisitForm.milestone,
          date: Timestamp.fromDate(new Date(editVisitForm.date)),
          description: editVisitForm.description.trim(),
          concern: editVisitForm.concern.trim(),
          cleared: editVisitForm.cleared,
          paidINR:
            editVisitForm.paidINR === "" ? 0 : Number(editVisitForm.paidINR),
          documents: editVisitForm.documents,
        },
      );
      showToast("Visit updated.");
      setEditingVisit(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setEditVisitSaving(false);
    }
  }, [editingVisit, editVisitForm, routeUid, showToast]);

  const handleToggleCleared = useCallback(
    async (orderId, visit) => {
      if (!canWrite) return;
      await updateDoc(
        doc(
          db,
          "users",
          routeUid,
          "qualityReports",
          orderId,
          "visits",
          visit.id,
        ),
        { cleared: !visit.cleared },
      );
    },
    [canWrite, routeUid],
  );

  const handleToggleFlagged = useCallback(
    async (orderId, visit) => {
      if (!isAdmin) return;
      const visitRef = doc(
        db,
        "users",
        routeUid,
        "qualityReports",
        orderId,
        "visits",
        visit.id,
      );
      const flagRef = doc(db, "qcFlagIndex", visit.id);
      if (visit.flagged) {
        await Promise.all([
          updateDoc(visitRef, { flagged: false }),
          deleteDoc(flagRef),
        ]);
      } else {
        const ownerSnap = await getDoc(doc(db, "users", routeUid));
        const ownerName = ownerSnap.data()?.name || routeUid;
        await Promise.all([
          updateDoc(visitRef, { flagged: true }),
          setDoc(flagRef, {
            ownerUid: routeUid,
            ownerName,
            orderId,
            visitId: visit.id,
            milestone: visit.milestone || "",
            concern: visit.concern || "",
            description: visit.description || "",
            date: visit.date || null,
            documents: visit.documents || [],
            status: "flagged",
            flaggedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }),
        ]);
      }
    },
    [isAdmin, routeUid],
  );

  const handleDeleteVisit = useCallback(
    async (orderId, visit) => {
      const key = `visit:${visit.id}`;
      if (deleteConfirm !== key) {
        setDeleteConfirm(key);
        return;
      }
      try {
        if (isAdmin) {
          await deleteDoc(
            doc(
              db,
              "users",
              routeUid,
              "qualityReports",
              orderId,
              "visits",
              visit.id,
            ),
          );
          setDeleteConfirm(null);
          showToast("Visit deleted.", "error");
        } else {
          // QC member: raise a delete request instead of deleting directly.
          // The visit stays fully active/interactive until admin approves.
          const ownerSnap = await getDoc(doc(db, "users", routeUid));
          const ownerName = ownerSnap.data()?.name || routeUid;
          await Promise.all([
            updateDoc(
              doc(
                db,
                "users",
                routeUid,
                "qualityReports",
                orderId,
                "visits",
                visit.id,
              ),
              { deleteRequested: true },
            ),
            setDoc(doc(db, "qcFlagIndex", `${visit.id}_delete`), {
              type: "delete_request",
              scope: "visit",
              ownerUid: routeUid,
              ownerName,
              orderId,
              visitId: visit.id,
              milestone: visit.milestone || "",
              concern: visit.concern || "",
              description: visit.description || "",
              date: visit.date || null,
              documents: visit.documents || [],
              status: "pending",
              requestedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
            }),
          ]);
          setDeleteConfirm(null);
          showToast("Delete request sent to admin.", "error");
        }
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [deleteConfirm, routeUid, showToast, isAdmin],
  );

  const handleCancelDeleteRequest = useCallback(
    async (orderId, visit) => {
      try {
        await Promise.all([
          deleteDoc(doc(db, "qcFlagIndex", `${visit.id}_delete`)),
          updateDoc(
            doc(
              db,
              "users",
              routeUid,
              "qualityReports",
              orderId,
              "visits",
              visit.id,
            ),
            { deleteRequested: false },
          ),
        ]);
        showToast("Delete request cancelled.");
      } catch (err) {
        showToast(err.message, "error");
      }
    },
    [routeUid, showToast],
  );

  // R2 file upload handlers — one per modal
  const handleAddVisitFileUpload = useCallback(
    async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      setUploadingFiles(true);
      try {
        const urls = await Promise.all(
          files.map((f) => uploadToR2(f, "quality-docs")),
        );
        setVisitForm((f) => ({ ...f, documents: [...f.documents, ...urls] }));
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setUploadingFiles(false);
        e.target.value = "";
      }
    },
    [uploadToR2, showToast],
  );

  const handleEditVisitFileUpload = useCallback(
    async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      setEditUploadingFiles(true);
      try {
        const urls = await Promise.all(
          files.map((f) => uploadToR2(f, "quality-docs")),
        );
        setEditVisitForm((f) => ({
          ...f,
          documents: [...f.documents, ...urls],
        }));
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setEditUploadingFiles(false);
        e.target.value = "";
      }
    },
    [uploadToR2, showToast],
  );

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    navigate("/");
  }, [navigate]);

  if (!authReady) return null;

  const isViewingOwn = currentUser?.uid === routeUid;

  return (
    <div className={styles.page}>
      {/* ── Top Bar ── */}
      <nav className={styles.topBar}>
        <a href="/" className={styles.topLogo}>
          <img src={LOGO_URL} alt="Synaptex" className={styles.logoImg} />
        </a>
        <div className={styles.navRight}>
          {isAdmin && (
            <button
              className={styles.navBtn}
              onClick={() => navigate("/adminconsole")}
            >
              Console
            </button>
          )}
          <button
            className={`${styles.navBtn} ${styles.navBtnActive}`}
            onClick={() => navigate(`/quality/${routeUid}`)}
          >
            Quality
          </button>
          <button className={styles.navBtn} onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </nav>

      {/* ── Welcome ── */}
      <div className={styles.welcomeBar}>
        <span className={styles.welcomeText}>
          {isViewingOwn ? "YOUR QC REPORTS" : "VIEWING QC REPORTS —"}{" "}
          {!isViewingOwn && (
            <span className={styles.welcomeName}>{userProfile?.name}</span>
          )}
        </span>
        <h1 className={styles.pageTitle}>Quality Control</h1>
      </div>

      <div className={styles.rule} />

      {/* ── Stats ── */}
      <div className={styles.statsRow}>
        {[
          { label: "Total Orders", value: stats.totalOrders, sub: "under QC" },
          {
            label: "Total Visits",
            value: stats.totalVisits,
            sub: "all inspections",
          },
          { label: "Open", value: stats.open, sub: "not yet cleared" },
          { label: "Cleared", value: stats.cleared, sub: "resolved visits" },
          {
            label: "Total Paid",
            value: `₹${stats.totalPaid.toLocaleString("en-IN")}`,
            sub: "QC spend so far",
          },
          { label: "Last 30 Days", value: stats.recent, sub: "recent visits" },
        ].map((s) => (
          <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub} />
        ))}
      </div>

      {/* ── Table wrapper ── */}
      <div className={styles.tableWrapper} style={{ maxWidth: "100%" }}>
        <div className={styles.tableControls}>
          <div className={styles.tableFilters}>
            <div className={styles.tableFilterGroup}>
              <span className={styles.tableFilterLabel}>Country</span>
              <SelectWrapper
                value={filterCountry}
                selectClassName={styles.tableFilterSelect}
                onChange={(e) => {
                  setFilterCountry(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                {presentCountries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </SelectWrapper>
            </div>
            <div className={styles.tableFilterGroup}>
              <span className={styles.tableFilterLabel}>Status</span>
              <SelectWrapper
                value={filterStatus}
                selectClassName={styles.tableFilterSelect}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="open">Open</option>
                <option value="cleared">Cleared</option>
              </SelectWrapper>
            </div>
          </div>
          {canWrite && (
            <button
              className={`${styles.btn} ${styles.btnPrimary} ${styles.tableAddBtn}`}
              onClick={() => setShowAddOrderModal(true)}
            >
              + Add Order
            </button>
          )}
        </div>

        <QualityTable
          scrollRef={scrollRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          paginatedOrders={paginatedOrders}
          visitsByOrder={visitsByOrder}
          expandedOrderId={expandedOrderId}
          canWrite={canWrite}
          isAdmin={isAdmin}
          deleteConfirm={deleteConfirm}
          currentPage={currentPage}
          ITEMS_PER_PAGE={ITEMS_PER_PAGE}
          onToggleExpand={toggleExpand}
          onOpenEditOrder={handleOpenEditOrder}
          onDeleteOrder={handleDeleteOrder}
          onOpenAddVisit={handleOpenAddVisit}
          onOpenEditVisit={handleOpenEditVisit}
          onDeleteVisit={handleDeleteVisit}
          onToggleCleared={handleToggleCleared}
          onToggleFlagged={handleToggleFlagged}
          onCancelDelete={() => setDeleteConfirm(null)}
          onCancelDeleteRequest={handleCancelDeleteRequest}
          onCancelOrderDeleteRequest={handleCancelOrderDeleteRequest}
          getOrderStats={getOrderStats}
        />
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* ── Modals ── */}
      {showAddOrderModal && (
        <AddOrderModal
          form={orderForm}
          setForm={setOrderForm}
          availableOrderOptions={availableOrderOptions}
          saving={savingOrder}
          onOrderSelect={(val) => handleOrderSelect(val, setOrderForm)}
          onSave={handleAddOrder}
          onClose={() => {
            setShowAddOrderModal(false);
            setOrderForm(EMPTY_ORDER_FORM);
          }}
        />
      )}

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          form={editOrderForm}
          setForm={setEditOrderForm}
          saving={editOrderSaving}
          onSave={handleSaveEditOrder}
          onClose={() => setEditingOrder(null)}
        />
      )}

      {addVisitOrder && (
        <AddVisitModal
          order={addVisitOrder}
          form={visitForm}
          setForm={setVisitForm}
          docInput={visitDocInput}
          setDocInput={setVisitDocInput}
          uploading={uploadingFiles}
          saving={savingVisit}
          onFileUpload={handleAddVisitFileUpload}
          onSave={handleAddVisit}
          onClose={() => setAddVisitOrder(null)}
        />
      )}

      {editingVisit && (
        <EditVisitModal
          visit={editingVisit}
          form={editVisitForm}
          setForm={setEditVisitForm}
          docInput={editVisitDocInput}
          setDocInput={setEditVisitDocInput}
          uploading={editUploadingFiles}
          saving={editVisitSaving}
          onFileUpload={handleEditVisitFileUpload}
          onSave={handleSaveEditVisit}
          onClose={() => setEditingVisit(null)}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={styles.toast}>
          <div
            className={
              toast.type === "error" ? styles.toastError : styles.toastSuccess
            }
          >
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
}
